class QuickFilterManager {
  constructor() {
    this.quickFilterButtons = document.querySelectorAll(".quick-filter-btn");
    this.facetForm = document.querySelector("#FacetFiltersForm");
    this.facetFormMobile = document.querySelector("#FacetFiltersFormMobile");
    this.quickFiltersSection = document.querySelector("#quick-filters-section");

    if (this.quickFilterButtons.length > 0) {
      this.init();
    }
  }

  init() {
    this.quickFilterButtons.forEach((btn) => {
      btn.addEventListener("click", (e) => this.handleQuickFilterClick(e));
    });

    // Listen for facet form changes to update quick filters
    if (this.facetForm) {
      this.facetForm.addEventListener("input", () => this.syncQuickFilters());
    }
    if (this.facetFormMobile) {
      this.facetFormMobile.addEventListener("input", () => this.syncQuickFilters());
    }
  }

  isRadioMode() {
    return this.quickFiltersSection?.dataset.filterType === "radio";
  }

  handleQuickFilterClick(e) {
    const btn = e.currentTarget;
    const filterParam = btn.dataset.filterParam;
    const filterValue = btn.dataset.filterValue;
    const isActive = btn.dataset.active === "true";
    const isRadio = this.isRadioMode();

    if (filterValue === "all" && filterParam === "all") {
      if (!isActive) {
        this.resetAllFilters(btn);
      }
      return;
    }

    if (isRadio) {
      this.handleRadioClick(btn, filterParam, filterValue, isActive);
    } else {
      this.handleCheckboxClick(btn, filterParam, filterValue, isActive);
    }
  }

  resetAllFilters(allBtn) {
    const filterList = document.querySelector(".quick-filters-list");
    const filterParam = filterList?.dataset.filterParam;

    if (filterParam) {
      if (this.facetForm) {
        this.facetForm
          .querySelectorAll(
            `input[name="${filterParam}"], input[name^="${filterParam}["], input[data-param-name="${filterParam}"], input[data-param-name^="${filterParam}["]`,
          )
          .forEach((input) => {
            input.checked = false;
            input.dataset.wasChecked = "false";
          });
      }

      if (this.facetFormMobile) {
        this.facetFormMobile
          .querySelectorAll(
            `input[name="${filterParam}"], input[name^="${filterParam}["], input[data-param-name="${filterParam}"], input[data-param-name^="${filterParam}["]`,
          )
          .forEach((input) => {
            input.checked = false;
            input.dataset.wasChecked = "false";
          });
      }
    }

    this.clearAllQuickFilterButtons();

    this.toggleButtonState(allBtn, true);

    this.triggerFormSubmit();
  }

  handleRadioClick(btn, filterParam, filterValue, isActive) {
    if (isActive) {
      // Clicking active radio - deselect it
      this.clearAllFiltersInGroup(filterParam);
      this.clearAllQuickFilterButtons();
      this.triggerFormSubmit();
    } else {
      // Select new radio - clear others first
      this.clearAllFiltersInGroup(filterParam);
      this.clearAllQuickFilterButtons();

      // Check the corresponding input in forms
      this.setFilterValue(filterParam, filterValue, true);
      this.toggleButtonState(btn, true);
      this.triggerFormSubmit();
    }
  }

  handleCheckboxClick(btn, filterParam, filterValue, isActive) {
    this.setFilterValue(filterParam, filterValue, !isActive);
    this.toggleButtonState(btn, !isActive);

    // Trigger through the checkbox itself for proper form handling
    const checkbox = this.facetForm?.querySelector(`input[name="${filterParam}"][value="${filterValue}"]`);
    if (checkbox) {
      checkbox.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  clearAllFiltersInGroup(filterParam) {
    // Clear all radio/checkbox inputs that belong to the same filter group
    const baseParam = filterParam.replace(/\[\]$/, "");

    // Desktop form
    if (this.facetForm) {
      this.facetForm
        .querySelectorAll(`input[name^="${baseParam}"], input[data-param-name^="${baseParam}"]`)
        .forEach((input) => {
          input.checked = false;
          input.dataset.wasChecked = "false";
        });
    }

    // Mobile form
    if (this.facetFormMobile) {
      this.facetFormMobile
        .querySelectorAll(`input[name^="${baseParam}"], input[data-param-name^="${baseParam}"]`)
        .forEach((input) => {
          input.checked = false;
          input.dataset.wasChecked = "false";
        });
    }
  }

  setFilterValue(filterParam, filterValue, checked) {
    // Set value in desktop form
    const desktopInput = this.facetForm?.querySelector(
      `input[name="${filterParam}"][value="${filterValue}"], input[data-param-name="${filterParam}"][value="${filterValue}"]`,
    );
    if (desktopInput) {
      desktopInput.checked = checked;
      if (checked) desktopInput.dataset.wasChecked = "true";
    }

    // Set value in mobile form
    const mobileInput = this.facetFormMobile?.querySelector(
      `input[data-param-name="${filterParam}"][value="${filterValue}"]`,
    );
    if (mobileInput) {
      mobileInput.checked = checked;
      if (checked) mobileInput.dataset.wasChecked = "true";
    }
  }

  clearAllQuickFilterButtons() {
    this.quickFilterButtons.forEach((btn) => {
      this.toggleButtonState(btn, false);
    });
  }

  triggerFormSubmit() {
    // Build params manually to handle radio filters correctly
    if (this.facetForm) {
      const formData = new FormData(this.facetForm);
      const params = new URLSearchParams();
      const processedRadioGroups = new Set();

      for (const [key, value] of formData.entries()) {
        const inputElement = this.facetForm.querySelector(`input[name="${key}"][value="${value}"]`);
        const isRadioInput = inputElement && inputElement.classList.contains("facets__radio");

        if (isRadioInput) {
          const inputParamName = inputElement.dataset.paramName;
          if (!processedRadioGroups.has(inputParamName) && inputElement.checked) {
            params.append(inputParamName, value);
            processedRadioGroups.add(inputParamName);
          }
        } else {
          params.append(key, value);
        }
      }

      const searchParams = params.toString();
      FacetFiltersForm.renderPage(searchParams, null);
    }
  }

  toggleButtonState(btn, isActive) {
    const indicator = btn.querySelector(".quick-filter-indicator");

    btn.dataset.active = isActive;

    if (isActive) {
      indicator?.classList.remove("tw-hidden");
    } else {
      indicator?.classList.add("tw-hidden");
    }
  }

  syncQuickFilters() {
    let hasActiveInQuickFilterGroup = false;

    this.quickFilterButtons.forEach((btn) => {
      const filterParam = btn.dataset.filterParam;
      const filterValue = btn.dataset.filterValue;

      // Skip "All Products" button during this pass
      if (filterValue === "all" && filterParam === "all") return;

      // Check both checkbox and radio inputs
      const desktopCheckbox = this.facetForm?.querySelector(`input[name="${filterParam}"][value="${filterValue}"]`);
      const desktopRadio = this.facetForm?.querySelector(
        `input[data-param-name="${filterParam}"][value="${filterValue}"]`,
      );
      const mobileCheckbox = this.facetFormMobile?.querySelector(
        `input[name="${filterParam}"][value="${filterValue}"]`,
      );
      const mobileRadio = this.facetFormMobile?.querySelector(
        `input[data-param-name="${filterParam}"][value="${filterValue}"]`,
      );

      const isChecked =
        desktopCheckbox?.checked || desktopRadio?.checked || mobileCheckbox?.checked || mobileRadio?.checked || false;

      if (isChecked) hasActiveInQuickFilterGroup = true;
      this.toggleButtonState(btn, isChecked);
    });

    // "All Products" is active only when NO filter in the quick filter group is selected
    const allBtn = document.querySelector(".quick-filter-btn--all");
    if (allBtn) {
      this.toggleButtonState(allBtn, !hasActiveInQuickFilterGroup);
    }
  }
}

// Make it available globally
window.QuickFilterManager = QuickFilterManager;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuickFilterManager();
  });
} else {
  new QuickFilterManager();
}


class QuickFiltersScroller {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.list = wrapper.querySelector('.quick-filters-list');
    this.arrowLeft = wrapper.querySelector('.qf-arrow-left');
    this.arrowRight = wrapper.querySelector('.qf-arrow-right');

    if (!this.list) return;

    this.bind();
    this.update();
    window.addEventListener('resize', () => this.update());
  }

  bind() {
    this.list.addEventListener('scroll', () => this.update());

    this.arrowLeft?.addEventListener('click', () => {
      this.list.scrollBy({ left: -300, behavior: 'smooth' });
    });

    this.arrowRight?.addEventListener('click', () => {
      this.list.scrollBy({ left: 300, behavior: 'smooth' });
    });
  }

  update() {
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    const scrollLeft = this.list.scrollLeft;

    const canScroll = maxScroll > 5;
    const canLeft = scrollLeft > 10;
    const canRight = scrollLeft < maxScroll - 10;

    this.wrapper.closest('.quick-filters')
      ?.classList.toggle('show-left', canScroll && canLeft);

    this.wrapper.closest('.quick-filters')
      ?.classList.toggle('show-right', canScroll && canRight);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('.quick-filters-wrapper')
    .forEach(wrapper => new QuickFiltersScroller(wrapper));
});